import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

/**
 * Tap-to-match instead of true drag-and-drop: far more reliable for
 * 3-5 year olds on mobile touchscreens.
 */
export default function Level4Matching({ questions, sound, onComplete }) {
  const [qIndex, setQIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [matched, setMatched] = useState([]); // leftLabels matched
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [wrongFlash, setWrongFlash] = useState(null);
  const startTimeRef = useRef(Date.now());

  const rawQ = questions[qIndex];
  const total = questions.length;

  const q = useMemo(() => {
    if (!rawQ) return null;
    return { ...rawQ, pairs: rawQ.pairs.slice(0, 3) };
  }, [rawQ]);

  const shuffledRight = useMemo(() => {
    if (!q) return [];
    return [...q.pairs].sort(() => Math.random() - 0.5);
  }, [q]);

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  // Updated handlers with proper scoring
  const handleLeftTap = useCallback((pair) => {
    if (matched.includes(pair.leftLabel)) return;
    sound.tap();
    setSelectedLeft(pair);
  }, [matched, sound]);

  const handleRightTap = useCallback((pair) => {
    if (!selectedLeft || matched.includes(pair.leftLabel)) return;

    const isCorrect = pair.leftLabel === selectedLeft.leftLabel;

    if (isCorrect) {
      sound.correct();
      const nextMatched = [...matched, pair.leftLabel];
      setMatched(nextMatched);
      setSelectedLeft(null);

      // Clean scoring: +1 only when a full question is completed
      if (nextMatched.length === q.pairs.length) {
        setCorrectCount((c) => c + 1);

        setTimeout(() => {
          if (qIndex + 1 < total) {
            setQIndex((i) => i + 1);
            setMatched([]);
          } else {
            const timeTakenSec = Math.round((Date.now() - startTimeRef.current) / 1000);
            onComplete({
              correctCount: qIndex + 1,   // Reliable integer count
              totalQuestions: total,
              timeTakenSec,
              hintsUsed: 0,
            });
          }
        }, 500);
      }
    } else {
      sound.wrong();
      setWrongFlash(pair.leftLabel);
      setTimeout(() => setWrongFlash(null), 400);
    }
  }, [selectedLeft, matched, q, qIndex, total, sound, onComplete]);

  if (!q) return null;

  return (
    <div className="flex flex-col items-center gap-5 py-6 px-4">
      <p className="text-base sm:text-lg font-bold text-cyan-100 text-center">{q.prompt}</p>
      <div className="flex gap-6 sm:gap-10 w-full max-w-sm justify-center">
        <div className="flex flex-col gap-3">
          {q.pairs.map((pair) => (
            <button
              key={pair.leftLabel}
              onClick={() => handleLeftTap(pair)}
              disabled={matched.includes(pair.leftLabel)}
              className={[
                "w-fit h-16 sm:h-20 px-2 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl shadow-sm transition-all",
                matched.includes(pair.leftLabel)
                  ? "bg-[#DCFCE7] opacity-50"
                  : selectedLeft?.leftLabel === pair.leftLabel
                  ? "bg-[#F3EEFF] ring-4 ring-[#7C3AED]"
                  : "bg-white",
              ].join(" ")}
              aria-label={pair.leftLabel}
            >
              {pair.left}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          {shuffledRight.map((pair) => (
            <button
              key={pair.rightLabel}
              onClick={() => handleRightTap(pair)}
              disabled={matched.includes(pair.leftLabel)}
              className={[
                "w-fit h-16 sm:h-20 px-2 rounded-2xl grid grid-cols-2 text-3xl sm:text-4xl shadow-sm transition-all",
                matched.includes(pair.leftLabel)
                  ? "bg-[#DCFCE7] opacity-50"
                  : wrongFlash === pair.leftLabel
                  ? "bg-[#FEE2E2] ring-4 ring-red-300"
                  : "bg-gray-800 text-gray-100",
              ].join(" ")}
              aria-label={pair.rightLabel}
            >
              {pair.right}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-cyan-100">Tap a picture, then tap its match ✨</p>
    </div>
  );
}